/**
 * PrinterConfigDialog
 * Dialog for creating/editing thermal printer configuration
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import {
  PrinterConfig,
  PrinterBrand,
  PRINTER_BRAND_LABELS,
  PrinterConnectionType,
  PaperWidth,
  CreatePrinterConfigData,
  UpdatePrinterConfigData,
} from '../../../types/printer';
import { printerService } from '../../../services/printerService';

interface PrinterConfigDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  providerId: number;
  config?: PrinterConfig | null;
}

const PrinterConfigDialog: React.FC<PrinterConfigDialogProps> = ({
  open,
  onClose,
  onSaved,
  providerId,
  config,
}) => {
  const isEditing = !!config;

  const [name, setName] = useState('');
  const [printerBrand, setPrinterBrand] = useState<PrinterBrand>(PrinterBrand.EPSON);
  const [connectionType, setConnectionType] = useState<PrinterConnectionType>(PrinterConnectionType.NETWORK);
  const [host, setHost] = useState('');
  const [port, setPort] = useState('9100');
  const [paperWidth, setPaperWidth] = useState<PaperWidth>(PaperWidth.MM80);
  const [isDefault, setIsDefault] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Populate form when editing
  useEffect(() => {
    if (config) {
      setName(config.name);
      setPrinterBrand(config.printerBrand);
      setConnectionType(config.connectionType);
      setHost(config.host ?? '');
      setPort(String(config.port ?? 9100));
      setPaperWidth(config.paperWidth);
      setIsDefault(config.isDefault);
    } else {
      setName('');
      setPrinterBrand(PrinterBrand.EPSON);
      setConnectionType(PrinterConnectionType.NETWORK);
      setHost('');
      setPort('9100');
      setPaperWidth(PaperWidth.MM80);
      setIsDefault(true);
    }
    setTestResult(null);
    setError(null);
  }, [config, open]);

  const isNetwork = connectionType === PrinterConnectionType.NETWORK;

  const handleSave = async () => {
    setError(null);

    if (!name.trim()) {
      setError('Nome é obrigatório');
      return;
    }

    if (isNetwork && !host.trim()) {
      setError('IP é obrigatório para conexão de rede');
      return;
    }

    setSaving(true);
    try {
      if (isEditing && config) {
        const data: UpdatePrinterConfigData = {
          name: name.trim(),
          printerBrand,
          connectionType,
          host: isNetwork ? host.trim() : null,
          port: isNetwork ? parseInt(port) || 9100 : null,
          paperWidth,
          isDefault,
        };
        await printerService.update(config.id, data);
      } else {
        const data: CreatePrinterConfigData = {
          providerId,
          name: name.trim(),
          printerBrand,
          connectionType,
          host: isNetwork ? host.trim() : null,
          port: isNetwork ? parseInt(port) || 9100 : null,
          paperWidth,
          isDefault,
        };
        await printerService.create(data);
      }
      onSaved();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao salvar configuração';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!config) return;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await printerService.testConnection(config.id);
      setTestResult(result);
    } catch {
      setTestResult({ success: false, message: 'Erro ao testar conexão' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditing ? 'Editar Impressora' : 'Configurar Impressora'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Name */}
          <Grid size={12}>
            <TextField
              label="Nome"
              placeholder="Ex: Impressora Recepção"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>

          {/* Brand */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Marca"
              select
              value={printerBrand}
              onChange={(e) => setPrinterBrand(e.target.value as PrinterBrand)}
              fullWidth
              size="small"
            >
              {Object.entries(PRINTER_BRAND_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Paper Width */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Largura do Papel"
              select
              value={paperWidth}
              onChange={(e) => setPaperWidth(e.target.value as PaperWidth)}
              fullWidth
              size="small"
            >
              <MenuItem value={PaperWidth.MM58}>58mm</MenuItem>
              <MenuItem value={PaperWidth.MM80}>80mm</MenuItem>
            </TextField>
          </Grid>

          {/* Connection Type */}
          <Grid size={12}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Tipo de Conexão
            </Typography>
            <ToggleButtonGroup
              value={connectionType}
              exclusive
              onChange={(_, val) => val && setConnectionType(val)}
              size="small"
              fullWidth
            >
              <ToggleButton value={PrinterConnectionType.NETWORK}>Rede</ToggleButton>
              <ToggleButton value={PrinterConnectionType.USB}>USB</ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {/* IP and Port (conditional) */}
          {isNetwork && (
            <>
              <Grid size={8}>
                <TextField
                  label="IP da Impressora"
                  placeholder="192.168.1.100"
                  value={host}
                  onChange={(e) => setHost(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  label="Porta"
                  value={port}
                  onChange={(e) => setPort(e.target.value)}
                  fullWidth
                  size="small"
                  type="number"
                />
              </Grid>
            </>
          )}

          {/* Default Switch */}
          <Grid size={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                />
              }
              label="Impressora padrão"
            />
          </Grid>

          {/* Test Connection Button (only for existing network configs) */}
          {isEditing && isNetwork && (
            <Grid size={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleTestConnection}
                  disabled={testing}
                  startIcon={testing ? <CircularProgress size={16} /> : undefined}
                >
                  {testing ? 'Testando...' : 'Testar Conexão'}
                </Button>
                {testResult && (
                  <Typography
                    variant="body2"
                    color={testResult.success ? 'success.main' : 'error.main'}
                  >
                    {testResult.message}
                  </Typography>
                )}
              </Box>
            </Grid>
          )}

          {/* Error */}
          {error && (
            <Grid size={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrinterConfigDialog;
